import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import type { GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js'
import type { FileInfo } from '@puppycad/proto'
import { downloadCadFile } from '@/services/api/cad-session'
import {
  analyzeImportedModel,
  createCachedAssemblyResult,
  planImportedModelLoad,
  parseAssemblyManifestGltf,
  parseGlb,
  createCachedPartResult,
  CompleteGlbSceneProjectionPlugin,
  CompleteGlbSceneImporter,
  disposeImportedModel,
  disposeImportedModelObjectTreeResources,
  DEFAULT_PARSED_GLB_CACHE_VARIANT,
} from '@puppycad/cad-core'
import type {
  AssemblyManifestCache,
  AssemblyModelCache,
  BRepRegistrar,
  ImportedAssemblyReferenceResolver,
  ImportedModelDisplayType,
  ImportedModelResult,
  ModelAssetFile,
  ParsedGlbCache,
  PartAssetCache,
} from '@puppycad/cad-core'
import { buildLegacyGlbResultFromParsedGltf } from './glb-download'
import type { ImportedModelLoadGuard } from './imported-model-load-guard'
import {
  isImportedModelLoadAbortedError,
  throwIfImportedModelRequestStale,
} from './imported-model-load-guard'
import { ImportedModelLoadTransaction } from './imported-model-load-transaction'
import { ImportedModelRequestRegistry } from './imported-model-request-registry'
import { loadAssemblyModel } from './assembly-loader'
import type { ImportedBrepRuntimeCache } from './imported-model-brep-runtime-cache'
import { loadPartModel } from './part-loader'
import { buildParsedGlbArtifactKey } from './imported-model-artifact-key'
import type {
  LoadedImportedModel,
  ProjectModelPuppyMateDocument,
} from '../../../types/services'

export type {
  LoadedImportedModel,
  ProjectModelPuppyMateDocument,
} from '../../../types/services'

export type ImportedModelFileBytesReader = (file: FileInfo) => Promise<Uint8Array>

/**
 * 为 `parsedGlbCache` 的 in-flight 复用构造 artifact key。
 * 跨 `imported-model-loader` 与 `assembly-loader` 必须使用完全一致的格式，
 * 以便两边发起的并发下载能共用同一个 registry promise。
 *
 * 实际实现抽离到 `./imported-model-artifact-key` 以避免 assembly-loader 反向 import
 * 本模块形成循环依赖；此处保留 re-export 兼容既有 import 路径。
 */
export { buildParsedGlbArtifactKey }

type LoadImportedModelOptions = {
  projectFiles: FileInfo[]
  version: string
  partAssetCache: PartAssetCache
  assemblyManifestCache: AssemblyManifestCache
  assemblyModelCache: AssemblyModelCache
  parsedGlbCache: ParsedGlbCache
  importedBrepRuntimeCache?: ImportedBrepRuntimeCache
  brepRegistrar?: BRepRegistrar
  knownDisplayType?: ImportedModelDisplayType | null
  guard?: ImportedModelLoadGuard
  projectId?: string
  selectedFileId?: string
  referenceResolver?: ImportedAssemblyReferenceResolver
  /**
   * 可选文件读取器。
   *
   * 设计说明：默认仍通过 `downloadCadFile` 从后端读取；轻量 ZIP 节点包会把 zip entry 映射成
   * 内存文件，复用同一套 GLB/sidecar 解析链路，避免在项目模型入口重复实现拓扑恢复。
   */
  readFileBytes?: ImportedModelFileBytesReader
  /**
   * 语义资产 staging 事务。
   * - 显式传入时（caller 控制生命周期）：loader 只负责 stage，commit / abort 归 caller；
   * - 未传入时：loader 内部创建 local 事务，成功即隐式 commit，失败即隐式 abort。
   *
   * 这是 Task 2 给 Task 3（host 侧 per-request ownership）留的 API seam；未升级的 caller
   * （例如 useProjectLoader）默认走隐式 commit-on-success 的兼容路径。
   */
  transaction?: ImportedModelLoadTransaction
  /**
   * 宿主共享的 request registry。传入后 GLB 下载+解析会走 in-flight artifact 复用：
   * 并发的多个 `loadImportedModel` 调用对同一 (uuid, version) 只触发一次 download+parse，
   * 避免 tab-switch 抢占时重复下载。未传入时走旧的直接下载路径（与历史语义一致）。
   */
  requestRegistry?: ImportedModelRequestRegistry
  /**
   * 依赖解析阶段 child-load 开始时触发的可选回调。用于宿主把 in-flight 的子装配体
   * 下载/解析 work 反映到 UI（例如点亮子 tab 的 spinner）。仅在真正会触发下载/解析
   * 的路径上 fire（命中 cache-hit / skip / missing 的早退路径不 fire），避免 spinner 闪烁。
   *
   * 与 `onChildResolveComplete` 必须成对——loader 使用 try/finally 保证在异常路径下
   * 也会 fire complete。宿主侧通常用 refcount 在并发时只触发一次最终 UI 翻转。
   */
  onChildResolveStart?: (uuid: string) => void
  /**
   * 与 `onChildResolveStart` 成对的完成回调。异常路径（stale guard / 下载失败 /
   * 嵌套 loadAssemblyModel 抛错）同样会 fire，由宿主侧 refcount 确保 spinner 正确归零。
   *
   * `outcome` 语义见 `assembly-loader.ts`：
   *   - `'staged'`：子资产已 stage 到当前事务（等待 commit）；
   *   - `'aborted'`：事务 aborted / 抛错冒泡；
   *   - `'missing'`：子引用无法解析。
   * 宿主可用 outcome 决定"现在就清 spinner"（aborted / missing）还是"等 cache 写入
   * 触发 subscription 再同步清零 + 回填 icon"（staged），从而把 icon 回填与 spinner
   * 清零耦合为一次原子 UI 翻转。
   */
  onChildResolveComplete?: (uuid: string, outcome: 'staged' | 'aborted' | 'missing') => void
}

function resolveLoadedModelDisplayType(
  knownDisplayType: ImportedModelDisplayType | null | undefined,
  nextDisplayType: ImportedModelDisplayType,
): ImportedModelDisplayType {
  if (knownDisplayType === 'assembly') {
    return 'assembly'
  }
  return nextDisplayType
}

async function downloadSourceGltf(
  sessionId: string,
  file: FileInfo,
  options: Pick<
    LoadImportedModelOptions,
    | 'version'
    | 'parsedGlbCache'
    | 'guard'
    | 'transaction'
    | 'requestRegistry'
    | 'readFileBytes'
  > & {
    readonly completeSceneProjection?: PuppyCADWeb.CompleteGlbSourceProjectionOptions
  },
): Promise<GLTF> {
  const selectionCacheKey = options.completeSceneProjection?.cacheKey
  if (selectionCacheKey !== undefined && selectionCacheKey.trim().length === 0) {
    throw new Error('完整 GLB 根选择策略必须提供非空 cacheKey')
  }
  const cacheVariant = selectionCacheKey ?? DEFAULT_PARSED_GLB_CACHE_VARIANT
  const cached = options.parsedGlbCache.get(file.uuid, options.version, cacheVariant)
  if (cached) {
    throwIfImportedModelRequestStale(options.guard)
    // 命中 cache 时也要把该 key tracked 到调用方的事务上——这样后续调用方如果
    // abort/rollback，也会把 parsedGlbCache 的这条 GLTF（可能 geometry 已被 staged
    // 资产共享）一并 evict，避免 use-after-dispose（bug C）。
    options.transaction?.trackParsedArtifact(file.uuid, options.version, cacheVariant)
    return cached
  }

  const registry = options.requestRegistry
  const artifactKey = buildParsedGlbArtifactKey(file.uuid, options.version, cacheVariant)
  let downloadPromise: Promise<GLTF> | undefined = registry?.getInFlightArtifact<GLTF>(artifactKey)
  if (!downloadPromise) {
    downloadPromise = (async () => {
      const loader = new GLTFLoader()
      const completeSceneProjection = options.completeSceneProjection
      if (completeSceneProjection) {
        loader.register((parser) => new CompleteGlbSceneProjectionPlugin(
          parser,
          completeSceneProjection.selection,
        ))
      }
      const bytes = options.readFileBytes
        ? await options.readFileBytes(file)
        : await downloadCadFile(sessionId, file.fileId, file.uuid)
      const gltf = await parseGlb(loader, bytes)
      // 事务已被 abort 时，晚到的 in-flight artifact 不能重新写回共享 cache。
      if (options.transaction?.isAborted !== true) {
        options.parsedGlbCache.set(file.uuid, options.version, gltf, cacheVariant)
      }
      return gltf
    })()
    if (registry) {
      registry.registerInFlightArtifact(artifactKey, downloadPromise)
    }
  }

  // guard stale 的判定与下载共用：每个 caller 都要在自己 await 回来后独立检查一次。
  // 同步抛错放在 await 之前，防止 guard 已 stale 却还 pay 下载成本。
  throwIfImportedModelRequestStale(options.guard)
  const gltf = await downloadPromise
  throwIfImportedModelRequestStale(options.guard)
  // 每个 caller（原始下载者与 in-flight 复用者）都要把 artifact track 到自己的 transaction，
  // 这样任一侧回滚都能正确 evict 自己这份所有权视角下的 parsedGlbCache。
  options.transaction?.trackParsedArtifact(file.uuid, options.version, cacheVariant)
  return gltf
}

/**
 * 导入模型加载入口。语义资产（PartAsset / AssemblyModelAsset）统一经由 transaction
 * staging 发布；artifact 缓存（ParsedGlbCache / AssemblyManifestCache）按复用策略
 * 仍直接读写，不进入 staging。
 *
 * 当调用方传入 `options.transaction` 时，loader 只负责 stage，commit / abort 由 caller
 * 控制（典型：Editor 主链路配合 request registry 的 preemption 语义）。未显式传入时，
 * loader 在本函数内部创建一个 local 事务并按 commit-on-success / abort-on-failure 的
 * 兼容语义处理（典型：`useProjectLoader` 这类 non-editor consumer）。
 */
export async function loadImportedModel(
  sessionId: string,
  file: FileInfo,
  options: LoadImportedModelOptions,
): Promise<LoadedImportedModel | null> {
  const callerProvidedTransaction = options.transaction !== undefined
  const transaction =
    options.transaction
    ?? new ImportedModelLoadTransaction({
      partAssetCache: options.partAssetCache,
      assemblyModelCache: options.assemblyModelCache,
      parsedGlbCache: options.parsedGlbCache,
    })

  try {
    const loaded = await loadImportedModelInternal(sessionId, file, {
      ...options,
      transaction,
    })
    if (!callerProvidedTransaction) {
      transaction.commit()
    }
    return loaded
      ? {
        ...loaded,
        partAssetCache: options.partAssetCache,
        importedBrepRuntimeCache: options.importedBrepRuntimeCache,
      }
      : loaded
  } catch (error) {
    if (!callerProvidedTransaction) {
      // loader 失败：把本地事务固化为 aborted，dispose 掉 staged 资源；不让半成品
      // 泄漏到共享 cache。aborted 错误（stale guard）同样走这条路径。
      transaction.abort()
    }
    if (isImportedModelLoadAbortedError(error)) {
      throw error
    }
    throw error
  }
}

/**
 * 为一个 complete loading unit 独立解析选中 occurrence 子树。
 *
 * complete 场景返回的 Object3D 会被项目节点直接持有和改父级，因此禁止通过全局 parsed cache
 * 或 in-flight registry 与其它 loading unit 共享 GLTF。文件字节仍可由上层 prepared snapshot 复用，
 * 但每个 unit 必须拥有独立的 Object3D 树。
 *
 * @param sessionId 下载会话 ID。
 * @param file complete 包内的源 GLB。
 * @param selection 准备阶段已经验证的根选择。
 * @param readFileBytes prepared snapshot 提供的文件读取器。
 * @param guard 当前请求有效性守卫。
 * @returns 当前 loading unit 独占的 GLTF。
 */
async function parseCompleteGlbSource(
  sessionId: string,
  file: FileInfo,
  selection: PuppyCAD.CompleteGlbRootNodeSelection,
  readFileBytes: ImportedModelFileBytesReader | undefined,
  guard: ImportedModelLoadGuard | undefined,
): Promise<GLTF> {
  let ownedGltf: GLTF | null = null
  throwIfImportedModelRequestStale(guard)
  const loader = new GLTFLoader()
  loader.register(parser => new CompleteGlbSceneProjectionPlugin(parser, selection))
  const bytes = readFileBytes
    ? await readFileBytes(file)
    : await downloadCadFile(sessionId, file.fileId, file.uuid)
  throwIfImportedModelRequestStale(guard)
  try {
    ownedGltf = await parseGlb(loader, bytes)
    throwIfImportedModelRequestStale(guard)
    const result = ownedGltf
    ownedGltf = null
    return result
  } finally {
    // parse 成功但请求已失效时，GLTF 尚未交给 importer，当前函数仍是唯一 owner。
    if (ownedGltf) {
      disposeImportedModelObjectTreeResources(ownedGltf.scene)
    }
  }
}

/**
 * 加载由 OpenProject 根节点显式标记的完整 GLB 场景包。
 *
 * 该入口复用普通导入链路的下载、parsed cache、in-flight request registry、guard 与事务，
 * 但为 GLTFLoader 注册 complete 单根投影插件，并跳过 part/assembly 猜测和旧模板组合，
 * 直接把投影后的原生 GLTF 交给 cad-core 接管。
 *
 * @param sessionId 下载上下文 ID。
 * @param packageFile 触发完整模式的项目包文件。
 * @param sourceGlbFile ZIP 中唯一 GLB 的虚拟文件。
 * @param options 缓存、事务、文件系统与显式语义依赖。
 * @returns 完整 GLB 加载结果。
 */
export async function loadCompleteGlbScene(
  sessionId: string,
  packageFile: ModelAssetFile,
  sourceGlbFile: FileInfo,
  options: PuppyCADWeb.LoadCompleteGlbSceneOptions,
): Promise<LoadedImportedModel> {
  if (packageFile.loadMode !== 'complete-glb-scene') {
    throw new Error(`完整 GLB 加载入口只接受 loadMode=complete-glb-scene：${packageFile.fileId}`)
  }

  const callerProvidedTransaction = options.transaction !== undefined
  const completeGlbRootInstanceId = packageFile.completeGlbRootInstanceId?.trim() ?? ''
  const transaction = options.transaction ?? new ImportedModelLoadTransaction({
    partAssetCache: options.partAssetCache,
    assemblyModelCache: options.assemblyModelCache,
    parsedGlbCache: options.parsedGlbCache,
  })
  let stage: PuppyCAD.CompleteGlbImportStage = 'parse-source-glb'
  let ownedResult: ImportedModelResult | null = null

  try {
    // 正式导入必须继续消费 prepare 阶段所属文件的版本内根身份，不能重新读取父装配 occurrence。
    if (!completeGlbRootInstanceId) {
      throw new Error(`完整 GLB 文件缺少根选择 instance_id：fileId=${packageFile.fileId}`)
    }
    if (options.occurrenceGraph.selectedRootInstanceId !== completeGlbRootInstanceId) {
      throw new Error(
        `完整 GLB occurrence 图根身份不一致：expected=${completeGlbRootInstanceId}, actual=${options.occurrenceGraph.selectedRootInstanceId}`,
      )
    }
    if (
      options.occurrenceGraph.selection.sceneIndex !== options.rootSelection.sceneIndex
      || options.occurrenceGraph.selection.nodeIndex !== options.rootSelection.nodeIndex
    ) {
      throw new Error(
        `完整 GLB 根选择与 occurrence 图不一致：selection=${options.rootSelection.sceneIndex}:${options.rootSelection.nodeIndex}, graph=${options.occurrenceGraph.selection.sceneIndex}:${options.occurrenceGraph.selection.nodeIndex}`,
      )
    }
    const gltf = await parseCompleteGlbSource(
      sessionId,
      sourceGlbFile,
      options.rootSelection,
      options.readFileBytes,
      options.guard,
    )
    stage = 'analyze-scene'
    const importer = new CompleteGlbSceneImporter()
    ownedResult = await importer.import({
      gltf,
      packageFile: {
        fileId: packageFile.fileId,
        uuid: packageFile.uuid,
        kind: packageFile.kind,
        size: packageFile.size,
      },
      sourceGlbFile,
      persistedNodeId: options.persistedNodeId,
      version: options.version,
      expectedSemanticKind: options.expectedSemanticKind,
      occurrenceGraph: options.occurrenceGraph,
      projectFiles: options.projectFiles,
      readFileBytes: options.readFileBytes,
      brepRegistrar: options.brepRegistrar,
      importedBrepRuntimeCache: options.importedBrepRuntimeCache,
      assertCurrent: () => throwIfImportedModelRequestStale(options.guard),
      sourceAssetKey: options.sourceAssetKey,
    })
    throwIfImportedModelRequestStale(options.guard)
    if (!callerProvidedTransaction) {
      transaction.commit()
    }

    const result = ownedResult
    ownedResult = null
    return {
      displayType: options.expectedSemanticKind,
      result,
      partAssetCache: options.partAssetCache,
      importedBrepRuntimeCache: options.importedBrepRuntimeCache,
    }
  } catch (error) {
    if (!callerProvidedTransaction) {
      transaction.abort()
    }
    if (isImportedModelLoadAbortedError(error)) {
      // cad-core 会给 complete 导入错误补充阶段上下文；UI 取消语义仍需恢复最接近的原始错误。
      if (error instanceof Error && isImportedModelLoadAbortedError(error.cause)) {
        throw error.cause
      }
      throw error
    }
    if (stage === 'parse-source-glb') {
      throw new Error(
        [
          '完整 GLB 场景加载失败',
          `stage=${stage}`,
          `packageFileId=${packageFile.fileId}`,
          `packageUuid=${packageFile.uuid}`,
          `sourceGlbFileId=${sourceGlbFile.fileId}`,
          `persistedNodeId=${options.persistedNodeId?.trim() || 'none'}`,
          `instanceId=${completeGlbRootInstanceId || 'none'}`,
        ].join(', '),
        { cause: error },
      )
    }
    throw error
  } finally {
    // importer 已交付但尚未越过最终 guard/事务发布点时，候选结果仍由当前调用独占。
    if (ownedResult) {
      disposeImportedModel(ownedResult)
    }
  }
}

async function loadImportedModelInternal(
  sessionId: string,
  file: FileInfo,
  options: LoadImportedModelOptions & { transaction: ImportedModelLoadTransaction },
): Promise<LoadedImportedModel | null> {
  const effectiveKnownDisplayType = options.knownDisplayType ?? null
  const scopeKey = options.transaction.publishScopeKey

  const cachedAssemblyAsset = options.assemblyModelCache?.get(
    file.uuid,
    options.version,
    scopeKey,
  )
  if (cachedAssemblyAsset) {
    throwIfImportedModelRequestStale(options.guard)
    return {
      displayType: 'assembly',
      result: createCachedAssemblyResult(cachedAssemblyAsset, file),
    }
  }

  const cachedPartAsset = options.partAssetCache.get(file.uuid, options.version, scopeKey)
  if (cachedPartAsset && effectiveKnownDisplayType !== 'assembly') {
    throwIfImportedModelRequestStale(options.guard)
    return {
      displayType: 'part',
      result: createCachedPartResult(cachedPartAsset, file),
    }
  }

  const sourceGltf = await downloadSourceGltf(sessionId, file, options)
  const analysis = analyzeImportedModel(sourceGltf, file, options.version)
  // 当宿主明确声明导入文档类型时，该语义应优先于 GLB 文件扩展名。
  // 插入预览链路传入的是 .glb 资产地址，但用户选择项仍携带 cad_part/cad_assembly 语义。
  const effectiveAnalysis =
    effectiveKnownDisplayType === 'assembly' || effectiveKnownDisplayType === 'part'
      ? { ...analysis, explicitSemantic: effectiveKnownDisplayType }
      : analysis
  const plan = planImportedModelLoad(effectiveAnalysis)
  const resolvedDisplayType = resolveLoadedModelDisplayType(effectiveKnownDisplayType, plan.displayType)

  if (plan.loadStrategy === 'assembly-structure') {
    const manifestResult = parseAssemblyManifestGltf(sourceGltf, file, options.version)
    const assemblyResult = await loadAssemblyModel(sessionId, file, {
      ...options,
      sourceGltf,
      manifestResult: {
        manifest: manifestResult.manifest,
        warnings: [...analysis.warnings, ...manifestResult.warnings],
        inlinePartSourceByNodeId: manifestResult.inlinePartSourceByNodeId,
        inlineAssemblySourceByNodeId: manifestResult.inlineAssemblySourceByNodeId,
      },
    })
    if (assemblyResult) {
      return {
        displayType: resolvedDisplayType,
        result: assemblyResult,
      }
    }
  }

  if (plan.loadStrategy === 'part-asset' || plan.loadStrategy === 'assembly-structure') {
    const partResult = await loadPartModel(sessionId, file, {
      projectFiles: options.projectFiles,
      version: options.version,
      partAssetCache: options.partAssetCache,
      importedBrepRuntimeCache: options.importedBrepRuntimeCache,
      brepRegistrar: options.brepRegistrar,
      readFileBytes: options.readFileBytes,
      sourceGltf,
      guard: options.guard,
      transaction: options.transaction,
    })
    if (partResult) {
      return {
        displayType: resolvedDisplayType,
        result: partResult,
      }
    }
  }

  return {
    displayType: resolvedDisplayType,
    result: buildLegacyGlbResultFromParsedGltf(sourceGltf, file),
  }
}
